import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '../Button/ButtonLink'
import { Icon } from '../Icon/Icon'
import { SlideAction } from '../SlideAction/SlideAction'
import type { IconName } from '../Icon/types'
import { StatusChip } from '../StatusChip/StatusChip'
import { cx } from '../utils/cx'
import { CodeViewer } from './CodeViewer'
import type { AccessState, DigitalKeyProps, KeyAccess } from './types'
import './DigitalKey.css'

const ACCESS_ICON: Record<AccessState, IconName> = { active: 'lock-open', opening: 'loader', opened: 'check-circle', returning: 'check-circle', error: 'x-circle' }

function slideLabel(access: KeyAccess, single: boolean, t: ReturnType<typeof useTranslation>['t']) {
  const accessName = typeof access.name === 'string' ? access.name.toLowerCase() : access.name
  return single ? t('v2.ds.digitalKey.deslizaAbrirPuerta') : t('v2.ds.digitalKey.deslizaAbrir', { name: accessName })
}

/** Puente visual entre “Abierta” y el control normal: el carril aparece lleno y se rebobina. */
function ReturningAccess({ access, single }: { access: KeyAccess; single: boolean }) {
  const { t } = useTranslation()
  const [leaving, setLeaving] = useState(false)
  useEffect(() => {
    const enter = window.requestAnimationFrame(() => setLeaving(true))
    return () => window.cancelAnimationFrame(enter)
  }, [])
  return (
    <div className={cx('hp-key__access-return', leaving && 'hp-key__access-return--leaving')} aria-live="polite">
      <div className="hp-key__access-return-status" aria-hidden="true">
        <Icon name="check-circle" size={32} />
        <span>{t('v2.ds.digitalKey.abierta')}</span>
      </div>
      <SlideAction label={slideLabel(access, single, t)} onComplete={() => undefined} returning className="hp-key__access" />
    </div>
  )
}

function AccessButton({ access, single, onOpen }: { access: KeyAccess; single: boolean; onOpen?: ((id: string) => void) | undefined }) {
  const { t } = useTranslation()
  const state = access.state ?? 'active'
  const label =
    state === 'opening'
      ? t('v2.ds.digitalKey.abriendo')
      : state === 'opened'
        ? t('v2.ds.digitalKey.abierta')
      : (access.label ?? (single ? t('v2.ds.digitalKey.abrirPuerta') : t('v2.ds.digitalKey.abrir', { name: access.name })))
  if (state === 'active') return <SlideAction label={slideLabel(access, single, t)} onComplete={() => onOpen?.(access.id)} className="hp-key__access" />
  if (state === 'returning') return <ReturningAccess access={access} single={single} />
  return (
    <div data-key-state={state} aria-busy={state === 'opening' || undefined} role="status" className={cx('hp-key__access', `hp-key__access--${state}`)}>
      <Icon name={ACCESS_ICON[state]} size={32} spin={state === 'opening'} />
      <span>{label}</span>
    </div>
  )
}

/** Llave digital (DESIGN.md §4): superficie propia con estados bloqueada, activa, abriendo, abierta, error y caducada. */
export function DigitalKey({
  state,
  title,
  validity,
  accesses = [],
  onOpen,
  onRetry,
  missing = [],
  lockedText,
  phone,
  code,
  codeValidity,
  night,
  compact,
  className,
}: DigitalKeyProps) {
  const { t } = useTranslation()
  const anyOpened = accesses.some((a) => a.state === 'opened' || a.state === 'returning')
  const errored = accesses.filter((a) => a.state === 'error')
  const single = accesses.length === 1

  if (compact) {
    if (accesses.length === 0 || state !== 'active') return null
    return (
      <div className={cx('hp-key hp-key--compact', className)} data-night-key={night ? '' : undefined}>
        <div className="hp-key__accesses">
          {accesses.map((access) => <AccessButton key={access.id} access={access} single={single} onOpen={onOpen} />)}
        </div>
      </div>
    )
  }

  return (
    <div
      role="group"
      aria-label={typeof title === 'string' ? title : t('v2.ds.digitalKey.tuLlave')}
      data-night-key={night ? '' : undefined}
      className={cx('hp-key', `hp-key--${state}`, anyOpened && 'hp-key--opened', errored.length > 0 && 'hp-key--error', className)}
    >
      {state === 'locked' && (
        <>
          <div className="hp-key__head hp-key__head--muted">
            <Icon name="lock" size={24} />
            <span className="hp-key__title">{t('v2.ds.digitalKey.bloqueada')}</span>
          </div>
          <p className="hp-key__text">{lockedText ?? t('v2.ds.digitalKey.seActivaraCuando')}</p>
          {missing.length > 0 && (
            <ul className="hp-key__missing">
              {missing.map((m, i) => (
                <li key={i}>
                  {m.href ? (
                    <a href={m.href} className="hp-key__missing-link">
                      {m.label}
                      <Icon name="chevron-right" size={18} />
                    </a>
                  ) : (
                    <button type="button" onClick={m.onClick} className="hp-key__missing-link">
                      {m.label}
                      <Icon name="chevron-right" size={18} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {state === 'active' && (
        <>
          <div className="hp-key__head">
            <Icon name="key" size={24} />
            <span className="hp-key__title">{title ?? t('v2.ds.digitalKey.tuLlave')}</span>
            {validity && (
              <StatusChip tone="success" icon="clock" className="hp-key__chip">
                {validity}
              </StatusChip>
            )}
          </div>
          <div className="hp-key__accesses">
            {accesses.map((a) => (
              <AccessButton key={a.id} access={a} single={single} onOpen={onOpen} />
            ))}
          </div>
          <div className="hp-key__feedback" aria-live="polite">
            {anyOpened && (
              <div className="hp-key__status hp-key__status--success">
                <Icon name="check-circle" size={22} />
                <span>{t('v2.ds.digitalKey.abiertaTexto')}</span>
              </div>
            )}
            {errored.map((a) => (
              <div key={a.id} className="hp-key__status hp-key__status--error">
                <Icon name="x-circle" size={22} />
                <div className="hp-key__status-body">
                  <span className="hp-key__status-title">{t('v2.ds.digitalKey.noAbre')}</span>
                  <span>{t('v2.ds.digitalKey.noAbreTexto')}</span>
                  <div className="hp-key__status-actions">
                    <SlideAction label={t('v2.ds.digitalKey.deslizaReintentar')} onComplete={() => onRetry?.(a.id)} />
                    {phone && (
                      <ButtonLink variant="secondary" icon="phone" fullWidth={false} href={`tel:${phone.replace(/\s/g, '')}`}>
                        {phone}
                      </ButtonLink>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {state === 'expired' && (
        <>
          <div className="hp-key__head hp-key__head--muted">
            <Icon name="lock" size={24} />
            <span className="hp-key__title">{t('v2.ds.digitalKey.terminada')}</span>
          </div>
          <p className="hp-key__text hp-key__text--muted">{t('v2.ds.digitalKey.terminadaTexto')}</p>
        </>
      )}

      {code && state === 'active' && <CodeViewer code={code} validity={codeValidity} />}
    </div>
  )
}
